import * as _ from "lodash"
import { updateVariants } from "@/vue/planning/logic/updateVariants.ts"
import { TRAIN_PACKAGE_FAMILIES, prestationMatchesOrigin, resolveTrainPackCodeForOrigin, variantBelongsToPackCode } from "@/config/trainPackageConfig"

const sortPrestationsLowestToHighestPrice = (prestations) => {
	if (prestations.length === 0) return prestations
	const sorted = [...prestations]
	sorted.sort((a, b) => {
		// shorting by lowest price, but if there is pricePerLogement === 0, we move it to the end of the array
		// Find the lowest non-zero price among all variants, or 0 if all are 0
		const getLowestPrice = (prestation) => {
			const nonZeroPrices = prestation.variants
				.map(v => Number(v.pricePerLogement))
				.filter(price => price > 0)
			return nonZeroPrices.length > 0 ? Math.min(...nonZeroPrices) : 0
		}
		
		const priceA = getLowestPrice(a)
		const priceB = getLowestPrice(b)
		
		// If both prices are 0, maintain their relative order
		if (priceA === 0 && priceB === 0) return 0
		
		// If only priceA is 0, move it to the end
		if (priceA === 0) return 1
		
		// If only priceB is 0, move it to the end
		if (priceB === 0) return -1
		
		// Normal price comparison for non-zero prices
		return priceA - priceB
	})
	return sorted
}

const removeDuplicatesPv = (prestations) => {
	// First, always process elements whose originalId is null/undefined (the parents) before children
	const parents = [];
	const children = [];

	prestations.forEach((prest) => {
		if (prest.originalId == null) {
			parents.push(prest);
		} else {
			children.push(prest);
		}
	});

	// Track which prestationIds have been kept (for both parents and children)
	const prestationIdSet = new Set();
	const keptPrestations = [];

	// Add parents first; they have no originalId and can't be skipped
	parents.forEach((prest) => {
		keptPrestations.push(prest);
		prestationIdSet.add(String(prest.prestationId));
	});

	// Only add children if their originalId does NOT exist among parents
	children.forEach((prest) => {
		// Be carefull, prest.originalId is a number, so we need to compare it as a string
		const hasParent = parents.some(parent => String(parent.prestationId) === String(prest.originalId));
		if (!hasParent) {
			keptPrestations.push(prest);
			prestationIdSet.add(String(prest.prestationId));
		}
	});

	return keptPrestations;
}

// PV FLEX is catalogued on a fusion lihe but describes the same room as the
// FIXE already on the page (shared prestIdFusion). Keep the FIXE card.
const collapsePvFlexDuplicates = (prestations) => {
	const ids = new Set(prestations.map((prest) => String(prest.prestationId)))
	return prestations.filter((prest) => {
		const pvVariants = (prest.variants || []).filter((variant) => variant.pvFlex)
		if (!pvVariants.length) return true

		const fusionTargets = new Set(
			pvVariants.map((variant) => String(variant.prestIdFusion)).filter(Boolean)
		)
		if (fusionTargets.has(String(prest.prestationId))) return true

		const fusedIntoAnotherCard = [...fusionTargets].some((id) => ids.has(id))
		return !fusedIntoAnotherCard
	})
}

const removeDuplicates = (prestations) => {
	prestations.forEach((prest) => {
		const map = {}
		prest.variants.forEach((variant) => {
			map[variant.id] = variant
		})
		prest.variants = Object.values(map).sort(
			(a, b) => a.pricePerLogement > b.pricePerLogement
		)
	})
	return prestations
}

// Function to filter images based on prestIdSource
function filterImagesByPrestIdSource(prestIdSources) {
	if (!prestIdSources || !prestIdSources.length) {
		console.error("filterImagesByPrestIdSource error ~ no prestIdSources")
		return window.prestImages
	}
	
	const images = window.prestImages
	if (!images || !Array.isArray(images)) {
		console.error("filterImagesByPrestIdSource error ~ no prestImages available")
		return []
	}
	
    return images.map(image => {
        if (!image || typeof image !== 'object') return {};
        
        const keys = Object.keys(image);
        const filteredKeys = keys.filter(key => 
            typeof image[key] === 'string' && 
            prestIdSources.some(id => id && image[key].includes(id))
        );

        return filteredKeys.reduce((obj, key) => {
            obj[key] = image[key];
            return obj;
        }, {});
    }).filter(image => Object.keys(image).length > 0);
}


const buildAvailableGalleryImages = (prestations) => {
	const liheImagesBasedOnSeason = window.seasonManager?.isSummer() ? window.lihe?.picturesLodgingSummer : window.lihe?.picturesLodgingWinter

	if(!prestations || !prestations.length) {
		console.error("buildAvailableGalleryImages error ~ no prestations")
		return window.srcObjectArray || liheImagesBasedOnSeason || []
	} 

	const prestIdSources = prestations.flatMap(prestation => 
		prestation?.variants?.map(variant => variant?.prestIdSource) || []
	).filter(Boolean);

	if(!prestIdSources || !prestIdSources.length) {
		console.error("buildAvailableGalleryImages error ~ no prestIdSources")
		return window.srcObjectArray || liheImagesBasedOnSeason || []
	} 

	const filteredImages = filterImagesByPrestIdSource(prestIdSources)

	if(!filteredImages || !filteredImages.length) {
		console.error("buildAvailableGalleryImages error ~ no filteredImages")
		return window.srcObjectArray || liheImagesBasedOnSeason || []
	} 

	const availableImages = [...(liheImagesBasedOnSeason || []), ...(filteredImages || [])].map((obj => {
		return {
		  ...obj,
		  type: 'image'
		}
	}))

	return availableImages
}

const emitBuiltImagesEvent = () => {
	window.dispatchEvent(new Event("availableImagesBuilt"))
}



export default {
	prestations: (state) => {
		let prestations = []
		
		window.lihe.availablePrestations = prestations
		if (state.loading) {
			// Avoid calling buildAvailableGalleryImages with an empty list during loading
			const liheImagesBasedOnSeason = window.seasonManager?.isSummer() ? window.lihe?.picturesLodgingSummer : window.lihe?.picturesLodgingWinter
			window.srcObjectArray = window.srcObjectArray || liheImagesBasedOnSeason || []
			window.dispatchEvent(new Event("availableImagesBuilt"))
			return prestations

		}

		// get prestation arrays
		const initialPrestations = _.cloneDeep(removeDuplicatesPv(state.initialPrestationsArray)).filter(p => p && typeof p === 'object' && !Array.isArray(p));
		
		// get maps
		const availabilityPrestationsMap = _.cloneDeep(
			state.prestMaps.seAvailabilities[state.packCode]
		)
		
		const quotePrestationsMap = _.cloneDeep(
			state.prestMaps.prices[state.packCode] || null
		)

		// Check for availability.
		// The same prestation can be in several packages, so
		// an availability prestation is enriched with initial prestation info
		if (
			!availabilityPrestationsMap ||
			Object.keys(availabilityPrestationsMap).length === 0
		) {
			window.lihe.availablePrestations = prestations
			return prestations
		}

		try {
			prestations = initialPrestations
				.map((initialPrest) => {
					if (!initialPrest || typeof initialPrest !== 'object') return null; 
					const criterias = Array.isArray(initialPrest.criterias) ? initialPrest.criterias : [];

					initialPrest.criteriasSet = new Set(criterias);
					initialPrest["variants"] = []

					// check if prestationId matches prestIdSource
					Object.values(availabilityPrestationsMap).forEach((availPrest) => {
						availPrest["totalMaxCapacity"] =
							availPrest.quantity * availPrest.maxCapacity

						// Source id is the catalog match. PV FLEX uses a different source id
						// on the fusion lihe, and points back at the FIXE via prestIdFusion.
						const isPvFlexSibling = availPrest.pvFlex
							&& String(initialPrest.prestationId) === String(availPrest.prestIdFusion)
						if (initialPrest.prestationId === availPrest.prestIdSource || isPvFlexSibling) {
							initialPrest["variants"].push(availPrest)
						}
					})

					// for variants from previous loop, add variants that have the same prestIdFusion but different unique id (pv flex case)
					const variantsToAdd = []
					initialPrest["variants"].forEach((variant) => {
						const sameFusionIdPrestations = Object.values(
							availabilityPrestationsMap
						).filter((availPrest) => {
							return (
								(availPrest.prestIdFusion === variant.prestIdFusion && availPrest.id !== variant.id)
							|| (availPrest.codePackage === 'HS' && availPrest.fusionId === variant.fusionId && availPrest.id === variant.id)
							)
						})
						variantsToAdd.push(...sameFusionIdPrestations)
					})

					initialPrest["variants"] = [
						...initialPrest["variants"],
						...variantsToAdd,
					]

					const selectedOrigin = state.flightOrigin?.split("-")[0] || ""
					initialPrest["variants"] = initialPrest["variants"].filter((variant) => {
						if (variant.codePackage && !variantBelongsToPackCode(variant.codePackage, state.packCode)) {
							return false
						}
						if (Array.isArray(variant.origins) && variant.origins.length && selectedOrigin) {
							return variant.origins.includes(selectedOrigin)
						}
						return true
					})
					
					// Remove initialPrest that have no variants
					if (initialPrest["variants"].length === 0) {
						return
					}
					
					return initialPrest
				})
				.filter((_) => _)
		} catch (err) {
			console.error(err)
		}

		// update / enrich with quote prices
		if (!quotePrestationsMap || Object.keys(quotePrestationsMap).length === 0) {
			// why do we have this case ?
		} else {
			try {
				// Precompute totalMaxCapacity for all quotePrestations.
				Object.values(quotePrestationsMap).forEach((quotePrest) => {
					quotePrest["totalMaxCapacity"] =
						quotePrest.quantity * quotePrest.maxCapacity
				})

				// Create a map for direct access.
				const quotePrestMap = {}
				Object.values(quotePrestationsMap).forEach((quotePrest) => {
					quotePrestMap[quotePrest.id] = quotePrest
				})

				prestations = prestations
					.map((prest) => {
						prest["variants"] = prest["variants"]
							.map((variant) => {
								const quotePrest = quotePrestMap[variant.id]
								if (quotePrest) {
									// Remove keys that do no exist in the quotePrest.
									const quotePrestFieldsSet = new Set(Object.keys(quotePrest))
									Object.keys(variant).forEach((field) => {
										if (!quotePrestFieldsSet.has(field)) {
											delete variant[field]
										}
									})

									// Assign all the keys of the variant to the values of the quotePrest.
									Object.assign(variant, quotePrest)
								}
								return variant
							})
							.filter(Boolean) // Using `Boolean` is a concise way to filter out falsy values.
						return prest
					})
					.filter(Boolean)
			} catch (err) {
				console.error(err)
			}
		}

		prestations = removeDuplicates(prestations)
		prestations = collapsePvFlexDuplicates(prestations)
		prestations = sortPrestationsLowestToHighestPrice(prestations)
		// prestations = process_CS_packages(prestations, state.content)

		window.srcObjectArray = buildAvailableGalleryImages(prestations)
		window.lihe.availablePrestations = prestations
		emitBuiltImagesEvent()
		window.availablePrests = prestations


		const enrichedPrestations = prestations.map(prest => {

			// --- PATCH: assign PFTT_7, PFTT_8, PFPTT_7 or PFPTT_8 for UK and NL Eurostar ---
			// Skipass 6 → 7-day stay (_7). Skipass 7 → 7 nights + 1 night near the station (_8).
			const language = state.language || ""
			const isEurostarStayLocale = language.includes("en") || language.includes("nl")
			prest.variants = prest.variants.map(variant => {
				if (
					isEurostarStayLocale &&
					variant.codePackage &&
					!['PFTTUK', 'PFPTTUK', 'PFTTNL', 'PFPTTNL'].includes(variant.codePackage) && // Avoid TNE sibling codes (UK / NL)
					(typeof variant.skipass !== 'undefined')
				) {
					let codeBase = null;
					if (variant.codePackage.startsWith('PFTT')) {
						codeBase = 'PFTT';
					} else if (variant.codePackage.startsWith('PFPTT')) {
						codeBase = 'PFPTT';
					}
					if (codeBase) {
						const skipassNum = Number(variant.skipass) + 1;
						const newPackCode = `${codeBase}_${skipassNum}`;
						if (newPackCode === `${codeBase}_7` || newPackCode === `${codeBase}_8`) {
							variant.codePackage = newPackCode;
						}
					}
				}
				return variant;
			});
			// --- END PATCH ---

			return {
				...prest,
				variants: prest.variants.length > 1 ? updateVariants(prest.variants) : prest.variants
			}
		})
		return enrichedPrestations
	},

	variants: (state, getters) => {
		const prestations = _.cloneDeep(getters.prestations)
		let variants = []

		prestations.forEach((prest) => {
			variants = [...variants, ...prest.variants]
		})

		const updatedVariants = updateVariants(variants) 
		
		return updatedVariants.length > 1 ? updatedVariants : variants
	},

	cheapestPrestationIndex: (state, getters) => {
		const prestations = getters.prestations
		let cheapestPrestationIndex
		let currentCheapestPrice
		prestations.forEach((prest, prest_i) => {
			prest.variants.forEach((variant) => {
				const pricePerLogement = variant.pricePerLogement
				if (!currentCheapestPrice) {
					cheapestPrestationIndex = prest_i
					currentCheapestPrice = pricePerLogement
				} else if (currentCheapestPrice > pricePerLogement) {
					currentCheapestPrice = pricePerLogement
				}
			})
		})

		return cheapestPrestationIndex
	},
}
